import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Bell, BellOff } from "lucide-react";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { useToast } from "@/hooks/use-toast";

export function NotificationSettings() {
  const { 
    isSupported, 
    isSubscribed, 
    permission, 
    subscribe, 
    unsubscribe 
  } = usePushNotifications();
  const { toast } = useToast();

  const handleToggle = async (enabled: boolean) => {
    try {
      if (enabled) {
        await subscribe();
        toast({
          title: "Benachrichtigungen aktiviert",
          description: "Du erhältst jetzt Push-Benachrichtigungen über neue Prämien."
        });
      } else {
        await unsubscribe();
        toast({
          title: "Benachrichtigungen deaktiviert",
          description: "Du erhältst keine Push-Benachrichtigungen mehr."
        });
      }
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Benachrichtigungen konnten nicht geändert werden.",
        variant: "destructive"
      });
    }
  };

  if (!isSupported) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BellOff className="h-5 w-5" />
            Push-Benachrichtigungen
          </CardTitle>
          <CardDescription>
            Push-Benachrichtigungen werden von deinem Browser nicht unterstützt.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Push-Benachrichtigungen
        </CardTitle>
        <CardDescription>
          Erhalte Benachrichtigungen über neue Prämien, Aktionen und wichtige Updates.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium">Benachrichtigungen aktivieren</h4>
            <p className="text-sm text-muted-foreground">
              {permission === 'granted' 
                ? 'Benachrichtigungen sind erlaubt' 
                : permission === 'denied'
                ? 'Benachrichtigungen sind blockiert'
                : 'Berechtigung erforderlich'
              }
            </p>
          </div>
          <Switch
            checked={isSubscribed}
            onCheckedChange={handleToggle}
            disabled={permission === 'denied'}
          />
        </div>
        
        {permission === 'denied' && (
          <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
            <p className="text-sm text-destructive">
              Benachrichtigungen sind in deinen Browser-Einstellungen blockiert. 
              Bitte aktiviere sie in den Einstellungen für diese Website.
            </p>
          </div>
        )}

        {isSubscribed && (
          <div className="p-3 bg-success/10 border border-success/20 rounded-lg">
            <p className="text-sm text-success-foreground">
              ✅ Du erhältst Push-Benachrichtigungen über neue Prämien und Aktionen.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}